/**
 * Putting a photograph on a member.
 *
 * The columns have existed since migration 0036 and until now there has
 * been no way to fill one in.
 *
 * ---------------------------------------------------------------------
 * IT RESIZES BEFORE IT SENDS
 *
 * A photograph taken on a phone is four thousand pixels wide and eight
 * megabytes. What it is used for is a 46px circle beside a member's name
 * on a bar terminal. Sending the original would mean the club pays to
 * store it, the terminal pays to download it on every check, and a
 * member of staff on a slow connection at the pool waits for it.
 *
 * So the file is drawn into a canvas at 512px square and re-encoded as a
 * JPEG at 82%, which is about sixty kilobytes and indistinguishable at
 * the size it is displayed. 512 rather than 128 because the same
 * photograph is the members' directory and a membership card, and
 * because upscaling later is not possible.
 *
 * Cropped to a square from the centre rather than squashed. Every place
 * this is shown is a circle, and a squashed face in a circle is worse
 * than a cropped one.
 *
 * ---------------------------------------------------------------------
 * AND IT SHOWS WHAT IT IS ABOUT TO SEND
 *
 * The crop is the surprise: somebody uploads a group photograph and gets
 * a shoulder. So the preview is the actual canvas output — what is
 * previewed IS what is uploaded, not the original file scaled by CSS,
 * which would show a face that the upload then cuts off.
 */

import { useRef, useState } from 'react';
import { api } from '../lib/api.ts';
import { useMutation } from '../lib/useApi.ts';
import { Face } from './Face.tsx';
import { ErrorBanner } from './ui.tsx';

/** What the API will take. Matches lib/objects.ts. */
const ACCEPT = 'image/jpeg,image/png,image/webp';
const EDGE = 512;
const QUALITY = 0.82;

/**
 * A file, as a square JPEG data URL.
 *
 * Rejects rather than resolves on anything that will not decode, so the
 * caller can say "that is not an image" instead of uploading nothing and
 * reporting success.
 */
function squareJpeg(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('That file is not an image the browser can open.'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = EDGE;
        canvas.height = EDGE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('This browser cannot resize the image.'));
          return;
        }

        // Centre crop: take the largest square the picture contains and
        // draw it to fill. Faces are near the middle of a portrait and
        // never at a corner.
        const edge = Math.min(img.width, img.height);
        const sx = (img.width - edge) / 2;
        const sy = (img.height - edge) / 2;

        // A photograph is a photograph, not a diagram: smoothing on, and
        // high, because the downscale is usually eight-to-one and the
        // browser's default gives a visibly crunchy result at that ratio.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, edge, edge, 0, 0, EDGE, EDGE);

        // Always JPEG, whatever went in. A PNG of a photograph is four
        // times the size for no visible difference, and transparency in
        // a portrait is not a thing anybody wants.
        resolve(canvas.toDataURL('image/jpeg', QUALITY));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function PhotoPicker({
  /** `/v1/people/:id/photo` or `/v1/member-accounts/:id/photo`. */
  endpoint,
  name,
  photoUri,
  label,
  hint,
  size = 84,
  canEdit,
  onChanged,
}: {
  endpoint: string;
  name: string;
  photoUri: string | null;
  label: string;
  hint?: string;
  size?: number;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation(async (image: string | null) =>
    api(endpoint, { method: 'PUT', body: image === null ? { photo_uri: null } : { image } }),
  );

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    try {
      const data = await squareJpeg(file);
      setPreview(data);
      const done = await save.run(data);
      if (done) {
        setPreview(null);
        onChanged();
      }
    } catch (e) {
      // The preview is cleared on failure. Leaving it up shows a
      // photograph that is not saved and looks exactly like one that is.
      setPreview(null);
      setProblem(e instanceof Error ? e.message : 'That file could not be used.');
    }
  };

  return (
    <div className="photo-picker">
      <Face name={name} photoUri={preview ?? photoUri} size={size} />

      <div className="photo-picker__body">
        <div className="field-label">{label}</div>
        {hint && <div className="hint">{hint}</div>}

        {problem && <div className="hint photo-picker__problem">{problem}</div>}
        <ErrorBanner error={save.error} />

        {canEdit && (
          <div className="actions" style={{ marginTop: 6 }}>
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => {
                void choose(e.target.files?.[0]);
                // Cleared so choosing the SAME file again still fires a
                // change event, which is what somebody does after a
                // failed upload.
                e.target.value = '';
              }}
            />
            <button
              className="secondary small"
              disabled={save.busy}
              onClick={() => input.current?.click()}
            >
              {save.busy ? 'Uploading…' : photoUri ? 'Replace' : 'Add a photo'}
            </button>
            {photoUri && !save.busy && (
              <button
                className="secondary small"
                onClick={async () => {
                  const done = await save.run(null);
                  if (done) onChanged();
                }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
